import { Suspense, lazy, memo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, ExternalLink, Inbox, PanelRight, FileText, Plus } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { sameOriginPath } from '@/shared/lib/safe-path';
import { onExternalLinkClick, openExternalLink } from '@/shared/lib/external-link';
import { useAiChatStore, type ChatMsg } from '../model/aiChatStore';
import type { AiRating, AiSuggestion } from '../api/ai';
import styles from './AIChat.module.css';
import { Scroller } from '@/shared/ui';

const DOWN_REASONS = ['Неточно', 'Не по теме', 'Устарело', 'Непонятно'];

const NEAR_BOTTOM_PX = 120;

const ChatMarkdown = lazy(() => import('./ChatMarkdown'));

const STREAM_PARSE_MS = 90;

function useStreamedText(text: string, streaming: boolean): string {
  const [shown, setShown] = useState(text);
  const latest = useRef(text);
  latest.current = text;
  useEffect(() => {
    if (!streaming) return;
    const id = setInterval(() => setShown(latest.current), STREAM_PARSE_MS);
    return () => {
      clearInterval(id);
      setShown(latest.current);
    };
  }, [streaming]);
  return streaming ? shown : text;
}

const AssistantText = memo(function AssistantText({ text, streaming }: { text: string; streaming?: boolean }) {
  const shown = useStreamedText(text, !!streaming);
  return (
    <Suspense fallback={<>{text}</>}>
      <ChatMarkdown text={shown} streaming={streaming} />
    </Suspense>
  );
});

interface AIChatProps {
  onRate: (messageId: string, rating: AiRating | null, reason?: string) => void;
  onNewChat: () => void;
}

export const AIChat: React.FC<AIChatProps> = ({ onRate, onNewChat }) => {
  const messages = useAiChatStore((s) => s.messages);
  const status = useAiChatStore((s) => s.status);
  const load = useAiChatStore((s) => s.load);
  const context = useAiChatStore((s) => s.context);
  const error = useAiChatStore((s) => s.error);

  const viewportRef = useRef<HTMLDivElement>(null);
  const lastLen = messages[messages.length - 1]?.content.length ?? 0;
  useEffect(() => {
    const v = viewportRef.current;
    if (!v) return;
    if (v.scrollHeight - v.scrollTop - v.clientHeight > NEAR_BOTTOM_PX) return;
    v.scrollTop = v.scrollHeight;
  }, [messages.length, lastLen, status]);

  return (
    <div className={styles.chat}>
      {load !== 'normal' && (
        <div className={cn(styles.banner, styles.load)}>
          {load === 'overloaded'
            ? 'Высокая нагрузка на ассистента — ответ может занять больше времени.'
            : 'Ассистент обрабатывает запросы, возможна небольшая задержка.'}
        </div>
      )}
      {context === 'warn' && (
        <div className={cn(styles.banner, styles.warn)}>
          Чат стал длинным — качество ответов может снижаться.
          <button className={styles.newChatLink} onClick={onNewChat}>
            <Plus size={14} /> Новый чат
          </button>
        </div>
      )}
      {error && <div className={cn(styles.banner, styles.error)}>{error}</div>}

      <Scroller className={styles.list} viewportClassName={styles.listViewport} viewportRef={viewportRef}>
        {messages.map((m) => (
          <MessageItem key={m.key} msg={m} onRate={onRate} />
        ))}
        {status === 'thinking' && (
          <div className={cn(styles.msg, styles.assistant)}>
            <div className={styles.thinking}>
              <span /> <span /> <span />
            </div>
          </div>
        )}
      </Scroller>
    </div>
  );
};

const MessageItem: React.FC<{ msg: ChatMsg; onRate: AIChatProps['onRate'] }> = ({ msg, onRate }) => {
  const navigate = useNavigate();
  const [showReasons, setShowReasons] = useState(false);

  if (msg.role === 'user') {
    return <div className={cn(styles.msg, styles.user)}>{msg.content}</div>;
  }

  const onSuggestion = (s: AiSuggestion) => {
    if (!s.href) return;
    const internal = sameOriginPath(s.href);
    if (internal) navigate(internal);
    else void openExternalLink(s.href);
  };

  const suggIcon = (t: AiSuggestion['type']) =>
    t === 'create_ticket' ? <Inbox size={14} /> : t === 'portal_link' ? <PanelRight size={14} /> : <FileText size={14} />;

  const sourceHrefs = new Set((msg.sources ?? []).map((s) => s.url).filter(Boolean));
  const suggestions = (msg.suggestions ?? []).filter(
    (s) => !(s.type === 'wiki_article' && s.href && sourceHrefs.has(s.href)),
  );

  return (
    <div className={cn(styles.msg, styles.assistant)}>
      <div className={styles.content}>
        <AssistantText text={msg.content} streaming={msg.streaming} />
      </div>

      {!msg.streaming && suggestions.length > 0 && (
        <div className={styles.suggestions}>
          {suggestions.map((s, i) => (
            <button key={i} className={styles.suggestion} onClick={() => onSuggestion(s)}>
              {suggIcon(s.type)} {s.label}
            </button>
          ))}
        </div>
      )}

      {!msg.streaming && msg.sources && msg.sources.length > 0 && (
        <div className={styles.sources}>
          {msg.sources.map((src) => {
            const internal = src.url ? sameOriginPath(src.url) : null;
            return (
              <a
                key={src.id}
                className={styles.source}
                href={src.url ?? '#'}
                target={internal ? undefined : '_blank'}
                rel={internal ? undefined : 'noopener noreferrer'}
                onClick={
                  internal
                    ? (e) => {
                        e.preventDefault();
                        navigate(internal);
                      }
                    : src.url
                      ? onExternalLinkClick(src.url)
                      : undefined
                }
              >
                <ExternalLink size={12} /> {src.title}
              </a>
            );
          })}
        </div>
      )}

      {!msg.streaming && msg.id && (
        <div className={styles.rating}>
          <button
            className={cn(styles.rateBtn, msg.rating === 'up' && styles.rateActive)}
            aria-label="Хороший ответ"
            onClick={() => onRate(msg.id!, msg.rating === 'up' ? null : 'up')}
          >
            <ThumbsUp size={14} />
          </button>
          <button
            className={cn(styles.rateBtn, msg.rating === 'down' && styles.rateActive)}
            aria-label="Плохой ответ"
            onClick={() => setShowReasons((v) => !v)}
          >
            <ThumbsDown size={14} />
          </button>
          {showReasons && (
            <div className={styles.reasons}>
              {DOWN_REASONS.map((r) => (
                <button
                  key={r}
                  className={styles.reason}
                  onClick={() => {
                    onRate(msg.id!, 'down', r);
                    setShowReasons(false);
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
